import React, { useState, useEffect } from 'react';
import { PopoverMenuButton } from 'app/src/components/PopoverMenu';
import { forTemplate } from '@trello/i18n';
import { AccountItem } from './AccountItem';
import { TrelloStorage } from '@trello/storage';
import { identityBaseUrl, siteDomain } from '@trello/config';
import { memberId } from '@trello/session-cookie';
import { avatarsFromAvatarUrl } from '@trello/members';
import styles from './AccountSwitcherMenuPopover.less';
import { Analytics } from '@trello/atlassian-analytics';
import { useAccountSwitcherQuery } from './AccountSwitcherQuery.generated';

const format = forTemplate('header_account_switcher');
// eslint-disable-next-line @trello/no-module-logic
const AaSwitcherUrlParams = new URLSearchParams({
  prompt: 'select_account',
  continue: `${siteDomain}/auth/atlassian/callback`,
  application: 'trello',
});
// eslint-disable-next-line @trello/no-module-logic
const AaSwitcherPageUrl = `${identityBaseUrl}/login?${AaSwitcherUrlParams.toString()}`;

interface AccountSwitcherMenuDispatchProps {
  onClickAddAccount: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => void;
  hasAaAccount: boolean | undefined;
}

interface AccountSwitcherStateProps {
  hasAddedAccount: boolean;
}

interface AccountSwitcherProps
  extends AccountSwitcherStateProps,
    AccountSwitcherMenuDispatchProps {}

export const AccountSwitcher: React.FunctionComponent<AccountSwitcherProps> = ({
  onClickAddAccount,
  hasAddedAccount,
  hasAaAccount,
}) => {
  const { data } = useAccountSwitcherQuery({
    variables: {
      memberId: memberId || 'me',
    },
  });
  if (!data?.member) {
    return null;
  }
  const avatarUrl =
    data.member.nonPublic?.avatarUrl ?? data.member.avatarUrl ?? undefined;
  const avatars = avatarUrl ? avatarsFromAvatarUrl(avatarUrl) : undefined;

  const SwitchAccounts = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    Analytics.sendUIEvent({
      action: 'clicked',
      actionSubject: 'link',
      source: 'memberMenuInlineDialog',
      actionSubjectId: 'settingsSwitchAccountsLink',
    });
    window.location.href = AaSwitcherPageUrl;
  };

  const hasClickedAddAccount = data.member.oneTimeMessagesDismissed?.includes(
    'headerMemberMenuAddAccount',
  );

  return (
    <>
      <AccountItem
        name={
          data.member.nonPublic?.fullName ?? data.member.fullName ?? undefined
        }
        email={data.member.email}
        id={data.member.id}
        avatarSource={data.member.avatarSource ?? undefined}
        avatars={avatars}
        initials={
          data.member.nonPublic?.initials ?? data.member.initials ?? undefined
        }
        username={data.member.username}
      ></AccountItem>
      {(data.member.isAaMastered || hasAaAccount) && hasAddedAccount && (
        <PopoverMenuButton
          // eslint-disable-next-line react/jsx-no-bind
          onClick={SwitchAccounts}
          title={format('switch-account')}
        ></PopoverMenuButton>
      )}
      <PopoverMenuButton onClick={onClickAddAccount} appendSeparator={true}>
        <span
          className={!hasClickedAddAccount ? styles.addAccountNew : ''}
          data-new-string={format('new')}
        >
          {format('add-another-account')}
        </span>
      </PopoverMenuButton>
    </>
  );
};

interface AccountSwitcherMenuProps extends AccountSwitcherMenuDispatchProps {}

export const AccountSwitcherMenuPopover: React.FunctionComponent<AccountSwitcherMenuProps> = ({
  onClickAddAccount,
  hasAaAccount,
}) => {
  const [hasAddedAccount, setHasAddedAccount] = useState(false);
  useEffect(() => {
    setHasAddedAccount(
      TrelloStorage.get('accountSwitcherAccountAdded') === 'true',
    );
  }, []);

  return (
    <AccountSwitcher
      onClickAddAccount={onClickAddAccount}
      hasAddedAccount={hasAddedAccount}
      hasAaAccount={hasAaAccount}
    ></AccountSwitcher>
  );
};
